/* Painting on the matrix.
   The notation is still the only thing a chart is written from. A stroke reads the three
   fields, moves one hand, and writes them back, so the grid is a second way to type and not
   a second source of truth: they cannot disagree. */

import { LABEL, WRITTEN, strokeNotes, type Notes, type WrittenAction } from "@preflop/core";
import { el } from "../../ui/dom";
import { ACTION_COLOR } from "../../ui/grid";
import { flash } from "./flash";
import { takesGtoWizard, type Arriving } from "./paste-gtow";
import { page } from "./state";

type BrushAction = WrittenAction | "erase";

// Which action a click paints and at what weight. "erase" clears the hand. Module level, so
// it outlives the redraw every stroke causes.
const brush: { action: BrushAction; weight: number } = { action: "raise", weight: 100 };

/** What typing and painting both report: the action written into, if one was. */
type OnInput = (action?: WrittenAction) => void;

// The textareas on screen and the callback that owns them, registered by noteFields. Null
// when no editor is up, which is also what disables painting.
let noteInputs: {
  notes: Notes;
  boxes: Record<WrittenAction, HTMLTextAreaElement>;
  onInput: OnInput;
} | null = null;

/** Both panels re-register the brush target if they put fields up. Left over from the last
    render, it would point at textareas nobody can see. */
export const clearNoteInputs = (): void => void (noteInputs = null);

export const canPaint = (): boolean => page().editMode && !!noteInputs;

/** A stroke on one hand, written into the fields on screen. From there the same callback
 *  typing uses carries it to the preview. False when nothing was painted. */
export function paintHand(hand: string, only: boolean): boolean {
  const t = noteInputs;
  if (!t) return false;
  const erase = brush.action === "erase";
  let next: Notes;
  try {
    next = strokeNotes(t.notes, hand, { action: brush.action, weight: brush.weight, erase, only });
  } catch (e) {
    flash(
      "The notation has to parse before the matrix can be painted: " +
        (e instanceof Error ? e.message : String(e)),
      "err",
    );
    return false;
  }
  for (const a of WRITTEN) {
    t.notes[a] = next[a];
    t.boxes[a].value = next[a];
  }
  t.onInput(brush.action === "erase" || only ? undefined : brush.action);
  return true;
}

/** The brush bar: the action a click paints, and how much of it. */
export function brushBar(): HTMLElement {
  const num = el("input", {
    type: "number",
    min: "0",
    max: "100",
    step: "0.1",
    value: String(brush.weight),
    ariaLabel: "Brush weight in percent",
  });
  const actionBtns = new Map<BrushAction, HTMLButtonElement>();
  const weightBtns = new Map<number, HTMLButtonElement>();

  const marks = (): void => {
    for (const [a, b] of actionBtns) b.classList.toggle("on", a === brush.action);
    for (const [w, b] of weightBtns)
      b.classList.toggle("on", brush.action !== "erase" && w === brush.weight);
    num.disabled = brush.action === "erase";
  };

  for (const a of [...WRITTEN, "erase"] as const) {
    const b = el(
      "button",
      {
        type: "button",
        class: "btn",
        title:
          a === "erase"
            ? "Clear the hands you paint over"
            : `Paint ${LABEL[a]} at the weight beside it`,
        onclick: () => {
          brush.action = a;
          marks();
        },
      },
      a === "erase" ? null : el("span", { class: "dot", style: { background: ACTION_COLOR[a] } }),
      a === "erase" ? "Erase" : LABEL[a],
    );
    actionBtns.set(a, b);
  }

  for (const w of [100, 75, 50, 25])
    weightBtns.set(
      w,
      el(
        "button",
        {
          type: "button",
          class: "btn",
          title: `Paint at ${w}% — the rest of the hand keeps what it had`,
          onclick: () => {
            brush.weight = w;
            num.value = String(w);
            marks();
          },
        },
        String(w),
      ),
    );

  // Marked but never rewritten while it is being typed into: putting the rounded value back
  // would eat the "." of a weight like 62.5.
  num.addEventListener("input", () => {
    const v = parseFloat(num.value);
    if (!isNaN(v) && v >= 0 && v <= 100) {
      brush.weight = v;
      marks();
    }
  });

  marks();
  // Two lines and not one wrapping line: which action, then how much of it.
  return el(
    "div",
    { class: "brush" },
    el("div", { class: "set" }, [...actionBtns.values()]),
    el(
      "div",
      { class: "set" },
      el("span", { class: "lab" }, "Weight"),
      [...weightBtns.values()],
      num,
      el("span", { class: "pc" }, "%"),
    ),
  );
}

/** What the three fields carry beyond the notation itself. */
export interface FieldExtras {
  /** What sits under an action's tag: the raise size, for one. */
  under?: Partial<Record<WrittenAction, HTMLElement | null>>;
  /** How often hero holds each hand here, for a pasted solver range to be read against. */
  arriving?: Arriving;
}

/** Three notation fields bound to a draft, each with whatever sits under its tag. */
export function noteFields(
  host: HTMLElement,
  notes: Notes,
  onInput: OnInput,
  extras: FieldExtras = {},
): void {
  const under = extras.under ?? {};
  const field = (a: WrittenAction): HTMLTextAreaElement => {
    const ta = el("textarea", {
      value: notes[a],
      spellcheck: false,
      placeholder: a === "allin" ? "leave empty if this action does not exist here" : "",
      ariaLabel: LABEL[a],
      dataset: { testid: `note-${a}` },
    });
    ta.addEventListener("input", () => {
      notes[a] = ta.value;
      onInput(a);
    });
    // A range copied out of GTO Wizard arrives converted; anything else is pasted as it is.
    takesGtoWizard(ta, extras.arriving);
    host.append(
      el(
        "div",
        { class: "field" },
        el(
          "div",
          { class: "lead" },
          el("span", { class: "tag", style: { color: ACTION_COLOR[a] } }, LABEL[a]),
          under[a],
        ),
        ta,
      ),
    );
    return ta;
  };
  // A brush stroke goes through the fields and through this same callback: the matrix is a
  // way of typing, and nothing downstream can tell the difference.
  noteInputs = {
    notes,
    boxes: { allin: field("allin"), raise: field("raise"), call: field("call") },
    onInput,
  };
}
